import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ShieldCheck, Users, Tag, DollarSign, Search, Crown, Loader2, Plus, Copy, Trash2,
  Ticket, Globe, Lock,
} from "lucide-react";
import { api } from "@/lib/api";
import { fmtMoney } from "@/lib/theme";
import { useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const inputCls = "mt-1.5 h-11 border-rig-borderStrong bg-rig-bg text-white placeholder:text-rig-mute";
const TABS = [
  { key: "users", label: "Users", icon: Users },
  { key: "pricing", label: "Pricing", icon: DollarSign },
  { key: "promos", label: "Promo Codes", icon: Tag },
];

function StatBox({ label, value }) {
  return (
    <div className="rounded-xl border border-rig-border bg-rig-surface p-4">
      <div className="font-display text-[10px] font-bold uppercase tracking-[0.18em] text-rig-dim">{label}</div>
      <div className="mt-1 font-display text-2xl font-bold text-white">{value}</div>
    </div>
  );
}

/* ---------------- Users ---------------- */
function UsersTab() {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setRows(null);
    try {
      setRows(await api(`/admin/users${q ? `?q=${encodeURIComponent(q)}` : ""}`));
    } catch (e) {
      toast.error(e?.message || "Could not load users");
      setRows([]);
    }
  }, [q]);

  useEffect(() => { load(); }, [load]);

  const setPro = async (u, body, msg) => {
    setBusy(u.user_id);
    try {
      await api(`/admin/users/${u.user_id}/pro`, { method: "POST", body });
      toast.success(msg);
      await load();
    } catch (e) {
      toast.error(e?.message || "Failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 rounded-xl border border-rig-borderStrong bg-rig-bg px-3">
        <Search className="h-4 w-4 text-rig-dim" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by email or name"
          data-testid="admin-user-search"
          className="h-11 border-0 bg-transparent text-white placeholder:text-rig-mute focus-visible:ring-0"
        />
      </div>
      {rows === null ? (
        <Skeleton className="h-64 w-full bg-rig-surface2" />
      ) : rows.length === 0 ? (
        <p className="py-10 text-center text-sm text-rig-mute">No users found.</p>
      ) : (
        <div className="space-y-3" data-testid="admin-users-list">
          {rows.map((u) => (
            <div key={u.user_id} className="rounded-2xl border border-rig-border bg-rig-surface p-4" data-testid={`admin-user-${u.user_id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-display text-base font-bold text-white">{u.name || "—"}</span>
                    {u.is_admin && <ShieldCheck className="h-4 w-4 shrink-0 text-rig-blue" title="Admin" />}
                    {u.is_pro && (
                      <span className="flex items-center gap-1 rounded-full border border-rig-accent/40 bg-rig-accent/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-rig-accent">
                        <Crown className="h-3 w-3" /> Pro
                      </span>
                    )}
                  </div>
                  <p className="truncate text-xs text-rig-dim">{u.email}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {busy === u.user_id ? (
                    <Loader2 className="h-4 w-4 animate-spin text-rig-accent" />
                  ) : (
                    <>
                      <Button onClick={() => setPro(u, { days: 30 }, "Pro granted · 30 days")} data-testid={`grant-30-${u.user_id}`} variant="outline" size="sm" className="h-8 border-rig-borderStrong bg-transparent text-xs text-white hover:bg-rig-bg hover:text-rig-accent">+30d</Button>
                      <Button onClick={() => setPro(u, { days: 365 }, "Pro granted · 1 year")} data-testid={`grant-365-${u.user_id}`} variant="outline" size="sm" className="h-8 border-rig-borderStrong bg-transparent text-xs text-white hover:bg-rig-bg hover:text-rig-accent">+1yr</Button>
                      <Button onClick={() => setPro(u, { lifetime: true }, "Lifetime Pro granted")} data-testid={`grant-life-${u.user_id}`} size="sm" className="h-8 bg-rig-accent text-xs font-bold text-rig-accentText hover:bg-rig-accentDark">Lifetime</Button>
                      <Button onClick={() => setPro(u, { revoke: true }, "Pro revoked")} data-testid={`revoke-${u.user_id}`} variant="outline" size="sm" className="h-8 border-rig-error/50 bg-transparent text-xs text-rig-error hover:bg-rig-error/10">Revoke</Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Pricing ---------------- */
function PricingTab() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api("/admin/pricing").then(setForm).catch(() => setForm({ monthly: 9.99, yearly: 79 })); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api("/admin/pricing", { method: "PUT", body: { monthly: Number(form.monthly), yearly: Number(form.yearly) } });
      toast.success("Pro prices updated");
    } catch (e) {
      toast.error(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <Skeleton className="h-40 w-full bg-rig-surface2" />;
  return (
    <div className="max-w-lg rounded-2xl border border-rig-border bg-rig-surface p-6">
      <p className="text-sm text-rig-dim">Set the base Pro price your customers pay. Public promo codes can discount these automatically at checkout.</p>
      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <div>
          <Label className="font-display text-xs uppercase tracking-widest text-rig-dim">Monthly ($)</Label>
          <Input type="number" step="0.01" value={form.monthly} onChange={(e) => setForm((f) => ({ ...f, monthly: e.target.value }))} data-testid="price-monthly-input" className={inputCls} />
        </div>
        <div>
          <Label className="font-display text-xs uppercase tracking-widest text-rig-dim">Yearly ($)</Label>
          <Input type="number" step="0.01" value={form.yearly} onChange={(e) => setForm((f) => ({ ...f, yearly: e.target.value }))} data-testid="price-yearly-input" className={inputCls} />
        </div>
      </div>
      <Button onClick={save} disabled={saving} data-testid="save-pricing-btn" className="mt-5 h-11 w-full bg-rig-accent font-display text-sm font-bold uppercase tracking-widest text-rig-accentText hover:bg-rig-accentDark">
        {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save Prices
      </Button>
    </div>
  );
}

/* ---------------- Promo codes ---------------- */
const emptyPromo = { code: "", label: "", plan: "monthly", price_usd: "6.99", duration_days: "", lifetime: false, max_uses: "", is_public: false, expires_at: "" };

function PromosTab() {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(emptyPromo);
  const [creating, setCreating] = useState(false);
  const [delTarget, setDelTarget] = useState(null);

  const load = useCallback(async () => {
    try { setRows(await api("/admin/promo-codes")); } catch { setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setCreating(true);
    try {
      const body = {
        code: form.code.trim() || undefined,
        label: form.label,
        plan: form.plan,
        price_usd: Number(form.price_usd || 0),
        lifetime: form.lifetime,
        duration_days: form.duration_days ? Number(form.duration_days) : undefined,
        max_uses: form.max_uses ? Number(form.max_uses) : undefined,
        is_public: form.is_public,
        expires_at: form.expires_at || undefined,
      };
      const created = await api("/admin/promo-codes", { method: "POST", body });
      toast.success(`Code ${created.code} created`);
      setForm(emptyPromo);
      await load();
    } catch (e) {
      toast.error(e?.message || "Could not create code");
    } finally {
      setCreating(false);
    }
  };

  const toggle = async (p, patch) => {
    try {
      await api(`/admin/promo-codes/${p.id}`, { method: "PATCH", body: patch });
      await load();
    } catch (e) {
      toast.error(e?.message || "Update failed");
    }
  };

  const remove = async () => {
    try {
      await api(`/admin/promo-codes/${delTarget.id}`, { method: "DELETE" });
      toast.success("Code deleted");
      await load();
    } catch (e) {
      toast.error(e?.message || "Delete failed");
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-5">
      {/* Create */}
      <div className="lg:col-span-2 rounded-2xl border border-rig-accent/40 bg-rig-surface p-5" data-testid="create-promo-card">
        <div className="flex items-center gap-2">
          <Plus className="h-4 w-4 text-rig-accent" />
          <h3 className="font-display text-sm font-bold uppercase tracking-widest text-white">New Code</h3>
        </div>
        <Label className="mt-4 block text-xs uppercase tracking-widest text-rig-dim">Code (blank = auto)</Label>
        <Input value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))} placeholder="FLEET699" data-testid="promo-code-input" className={`${inputCls} font-display tracking-widest`} />
        <Label className="mt-3 block text-xs uppercase tracking-widest text-rig-dim">Label</Label>
        <Input value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} placeholder="Acme Trucking discount" data-testid="promo-label-input" className={inputCls} />
        <Label className="mt-3 block text-xs uppercase tracking-widest text-rig-dim">Plan</Label>
        <div className="mt-1.5 flex gap-2">
          {["monthly", "yearly"].map((pl) => (
            <button key={pl} onClick={() => setForm((f) => ({ ...f, plan: pl }))} data-testid={`promo-plan-${pl}`} className={`flex-1 rounded-xl border py-2.5 font-display text-xs font-bold uppercase tracking-widest transition-colors ${form.plan === pl ? "border-rig-accent bg-rig-accent/10 text-rig-accent" : "border-rig-borderStrong text-rig-dim hover:text-white"}`}>{pl}</button>
          ))}
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <div>
            <Label className="block text-xs uppercase tracking-widest text-rig-dim">Price ($ · 0 = free)</Label>
            <Input type="number" step="0.01" value={form.price_usd} onChange={(e) => setForm((f) => ({ ...f, price_usd: e.target.value }))} data-testid="promo-price-input" className={inputCls} />
          </div>
          <div>
            <Label className="block text-xs uppercase tracking-widest text-rig-dim">Max uses</Label>
            <Input type="number" value={form.max_uses} onChange={(e) => setForm((f) => ({ ...f, max_uses: e.target.value }))} placeholder="∞" data-testid="promo-maxuses-input" className={inputCls} />
          </div>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <div>
            <Label className="block text-xs uppercase tracking-widest text-rig-dim">Duration (days)</Label>
            <Input type="number" value={form.duration_days} onChange={(e) => setForm((f) => ({ ...f, duration_days: e.target.value }))} placeholder={form.plan === "yearly" ? "365" : "30"} disabled={form.lifetime} data-testid="promo-duration-input" className={inputCls} />
          </div>
          <div>
            <Label className="block text-xs uppercase tracking-widest text-rig-dim">Expires (optional)</Label>
            <Input type="date" value={form.expires_at} onChange={(e) => setForm((f) => ({ ...f, expires_at: e.target.value }))} data-testid="promo-expires-input" className={inputCls} />
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between rounded-xl border border-rig-border bg-rig-bg px-4 py-3">
          <span className="text-sm text-white">Lifetime Pro</span>
          <Switch checked={form.lifetime} onCheckedChange={(v) => setForm((f) => ({ ...f, lifetime: v }))} data-testid="promo-lifetime-toggle" />
        </div>
        <div className="mt-2 flex items-center justify-between rounded-xl border border-rig-border bg-rig-bg px-4 py-3">
          <div>
            <span className="text-sm text-white">Public discount</span>
            <p className="text-[11px] text-rig-mute">Auto-shows on the upgrade page for everyone</p>
          </div>
          <Switch checked={form.is_public} onCheckedChange={(v) => setForm((f) => ({ ...f, is_public: v }))} data-testid="promo-public-toggle" />
        </div>
        <Button onClick={create} disabled={creating} data-testid="create-promo-btn" className="mt-4 h-11 w-full bg-rig-accent font-display text-sm font-bold uppercase tracking-widest text-rig-accentText hover:bg-rig-accentDark">
          {creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Ticket className="mr-2 h-4 w-4" />} Create Code
        </Button>
      </div>

      {/* List */}
      <div className="lg:col-span-3 space-y-3">
        {rows === null ? (
          <Skeleton className="h-64 w-full bg-rig-surface2" />
        ) : rows.length === 0 ? (
          <p className="py-10 text-center text-sm text-rig-mute">No codes yet. Create one to hand out to a client.</p>
        ) : (
          rows.map((p) => (
            <div key={p.id} className="rounded-2xl border border-rig-border bg-rig-surface p-4" data-testid={`promo-row-${p.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <button onClick={() => { navigator.clipboard?.writeText(p.code); toast.success("Code copied"); }} data-testid={`copy-promo-${p.id}`} className="flex items-center gap-1.5 font-display text-lg font-bold tracking-widest text-rig-accent hover:opacity-80">
                      {p.code} <Copy className="h-3.5 w-3.5 text-rig-dim" />
                    </button>
                    {p.is_public ? (
                      <span className="flex items-center gap-1 rounded-full border border-rig-blue/40 bg-rig-blue/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-rig-blue"><Globe className="h-3 w-3" /> Public</span>
                    ) : (
                      <span className="flex items-center gap-1 rounded-full border border-rig-border px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-rig-dim"><Lock className="h-3 w-3" /> Private</span>
                    )}
                    {!p.active && <span className="rounded-full border border-rig-error/40 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-rig-error">Off</span>}
                  </div>
                  <p className="mt-1 text-xs text-rig-dim">
                    {p.label ? `${p.label} · ` : ""}
                    {p.lifetime ? "Lifetime" : `${p.duration_days}d`} {p.plan} ·{" "}
                    <span className="text-white">{Number(p.price_usd) <= 0 ? "FREE" : fmtMoney(p.price_usd)}</span> ·{" "}
                    used {p.used_count}{p.max_uses != null ? ` / ${p.max_uses}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] uppercase tracking-widest text-rig-dim">Active</span>
                    <Switch checked={!!p.active} onCheckedChange={(v) => toggle(p, { active: v })} data-testid={`toggle-active-${p.id}`} />
                  </div>
                  <Button onClick={() => setDelTarget(p)} data-testid={`delete-promo-${p.id}`} variant="outline" size="sm" className="h-8 border-rig-error/50 bg-transparent text-rig-error hover:bg-rig-error/10">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <ConfirmDialog
        open={!!delTarget}
        onOpenChange={(o) => !o && setDelTarget(null)}
        title={`Delete ${delTarget?.code || "code"}?`}
        description="This code will stop working immediately. Redemptions already granted stay active."
        confirmLabel="Delete"
        destructive
        onConfirm={remove}
        testID="delete-promo-confirm"
      />
    </div>
  );
}

/* ---------------- Page ---------------- */
export default function Admin() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState("users");
  const [stats, setStats] = useState(null);

  useEffect(() => {
    document.title = "Admin · RigBooks";
    if (user && !user.is_admin) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  useEffect(() => { api("/admin/stats").then(setStats).catch(() => {}); }, []);

  if (!user?.is_admin) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <ShieldCheck className="h-7 w-7 text-rig-blue" />
        <div>
          <h1 className="font-display text-2xl font-bold uppercase tracking-wide text-white">Admin Console</h1>
          <p className="text-sm text-rig-dim">Manage users, Pro pricing, and discount codes.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatBox label="Users" value={stats?.total_users ?? "—"} />
        <StatBox label="Pro Users" value={stats?.pro_users ?? "—"} />
        <StatBox label="Fleets" value={stats?.fleets ?? "—"} />
        <StatBox label="Active Codes" value={stats?.active_codes ?? "—"} />
      </div>

      <div className="flex gap-1 border-b border-rig-border">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            data-testid={`admin-tab-${key}`}
            className={`relative flex items-center gap-2 px-4 py-2.5 font-display text-xs font-bold uppercase tracking-widest transition-colors ${tab === key ? "text-rig-accent" : "text-rig-dim hover:text-white"}`}
          >
            <Icon className="h-4 w-4" /> {label}
            {tab === key && <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-rig-accent" />}
          </button>
        ))}
      </div>

      {tab === "users" && <UsersTab />}
      {tab === "pricing" && <PricingTab />}
      {tab === "promos" && <PromosTab />}
    </div>
  );
}
